import {
  EQUIPMENT_COMPONENT_LABELS,
  findEquipmentEntry,
  QUANTUM_STABILIZER_ID,
  QUANTUM_STABILIZER_NAME,
} from '../equipment/catalog';
import { findArtifact } from './artifacts';
import { itemAssetKey } from './asset-keys';
import { cardDefinition } from './cards';
import { findItemDefinition, ITEM_DEFINITIONS, type ItemDefinition } from './definitions';
import { jewelDefinition } from './jewels';
import { findPetFood, findPetUnlockConsumable } from './pet-consumables';
import { findPet } from './pets';
import { findSkinDefinition } from './skins';

/**
 * The ONE place a definition id becomes something a player may read.
 *
 * Every surface goes through here - the inventory, the marketplace, the direct
 * trade, the history, a future drop toast, a future gacha result. A second map
 * in a component is how `artifact_guns` eventually leaks onto a card that
 * nobody re-checked.
 *
 * ## Where the names come from
 *
 * Not invented here. Artifacts, pets, foods, energy spheres, paint buckets and
 * cannons already carry their names in their own catalogs, and this reads them
 * so there is exactly one definition of "what is this called". Only ships are
 * given a name in this file, because they have never had one - and even that is
 * a placeholder rather than content: `Nave 7`, not a name somebody has to
 * approve.
 *
 * ## The fallback is presentable, never the id
 *
 * A definition this does not recognise is still a definition somebody owns, and
 * showing them `zztest_relic` is worse than showing them "Item desconhecido".
 * The raw id goes to a development warning instead, where it belongs.
 */

/** Ships are `ship1`..`ship20` and have no lore name yet. */
const SHIP_ID = /^ship(\d+)$/;

/**
 * Definitions that have LEFT the game but still appear in history.
 *
 * They are gone from `PET_DEFINITIONS`, so nothing else can name them - and an
 * append-only inventory history keeps referencing them forever. A player
 * reading an old row deserves the name the pet actually had.
 */
const RETIRED_NAMES: Readonly<Record<string, string>> = Object.freeze({
  pet_mecha_warrior_anomaly: 'Mecha Guerreiro',
});

/** Cannons read their rarity, which IS how the game refers to them. */
const CANNON_RARITY_NAMES: Readonly<Record<string, string>> = Object.freeze({
  excellent: 'Excellent',
  perfect: 'Perfect',
  anomaly: 'Anomaly',
  prismatic: 'Prismatic',
});

/** What a player is shown when nothing in the catalog answers. */
export const UNKNOWN_ITEM_NAME = 'Item desconhecido';

const warned = new Set<string>();

/**
 * Warns once per unknown id, for whoever is developing.
 *
 * Once, because a grid of forty unknown cards should produce one line rather
 * than forty.
 *
 * Reached through `globalThis` rather than by importing `console`, because this
 * package is platform-neutral by contract (`"types": []`): it compiles with no
 * browser and no Node globals, and a bare `console` would be a compile error
 * here for exactly that reason. A host that has no console simply gets silence,
 * and the player still sees a presentable name either way.
 */
function warnUnknown(definitionId: string): void {
  if (warned.has(definitionId)) {
    return;
  }
  warned.add(definitionId);
  const host = globalThis as { console?: { warn?: (message: string) => void } };
  host.console?.warn?.(
    `[display-names] no display name for "${definitionId}" - showing "${UNKNOWN_ITEM_NAME}". ` +
      'Add it to packages/shared/src/inventory/display-names.ts.',
  );
}

/**
 * The player-facing name of one definition.
 *
 * Never returns an id. Resolution order follows how specific the catalog is:
 * a pet, an artifact, a food, an unlock consumable, a cannon, a ship, and then
 * the fallback.
 */
export function itemDisplayName(definitionId: string): string {
  const retired = RETIRED_NAMES[definitionId];
  if (retired) {
    return retired;
  }
  if (definitionId === QUANTUM_STABILIZER_ID) {
    return QUANTUM_STABILIZER_NAME;
  }
  // A component is called what the panel calls it - "Núcleo de Energia" - and
  // nothing more. The rarity is already the card's border and the level is
  // already its badge; repeating either in the name would say it twice.
  const equipment = findEquipmentEntry(definitionId);
  if (equipment) {
    return EQUIPMENT_COMPONENT_LABELS[equipment.component];
  }
  // A skin carries its own display name in the content, because it is the only
  // thing about it a player reads: there is no rarity ladder and no level.
  const skin = findSkinDefinition(definitionId);
  if (skin) {
    return skin.displayName;
  }
  /*
   * Uma joia diz o seu nome quando o tem, e cala-se quando não tem.
   *
   * Só a peça soberana foi nomeada - "Coração Primordial", decidido pelo dono.
   * As catorze de socket não têm nome, e inventar um aqui seria decidir
   * conteúdo num ficheiro de apresentação. Nenhuma delas é obtenível, todas
   * estão desativadas no catálogo, e a prova em `display-names.test.ts` exige
   * que quem não tem nome esteja desativado - portanto um jogador nunca lê um
   * id, que é a regra que este ficheiro existe para garantir.
   */
  const jewel = jewelDefinition(definitionId);
  if (jewel?.displayName) {
    return jewel.displayName;
  }
  /*
   * Uma carta chama-se o que o catálogo aprovado diz (gate V28H.C).
   *
   * Lido de `cards.ts` e nunca escrito aqui, exactamente como o nome de uma
   * skin ou de uma joia: o nome é conteúdo, e uma segunda cópia dele neste
   * ficheiro seria a que fica para trás quando a primeira mudar.
   *
   * As dezasseis têm nome, portanto isto nunca cai para o desconhecido - ao
   * contrário das joias, onde catorze são rascunho sem nome.
   */
  const card = cardDefinition(definitionId);
  if (card) {
    return card.displayName;
  }

  const pet = findPet(definitionId);
  if (pet) {
    return pet.name;
  }
  const artifact = findArtifact(definitionId);
  if (artifact) {
    return `Artefato ${artifact.name}`;
  }
  const food = findPetFood(definitionId);
  if (food) {
    return food.name;
  }
  // Energy spheres and paint buckets already carry a full name, including the
  // colour or finish they unlock.
  const unlock = findPetUnlockConsumable(definitionId);
  if (unlock) {
    return unlock.name;
  }

  const definition = findItemDefinition(definitionId);
  if (definition?.category === 'cannon') {
    const rarity = CANNON_RARITY_NAMES[definition.rarity];
    if (rarity) {
      return `Canhão ${rarity}`;
    }
  }
  const ship = SHIP_ID.exec(definitionId);
  if (ship?.[1]) {
    // A placeholder, deliberately. Naming twenty ships is content, not a
    // rendering concern, and `Nave 7` is at least honest about that.
    return `Nave ${ship[1]}`;
  }

  warnUnknown(definitionId);
  return UNKNOWN_ITEM_NAME;
}

/**
 * The evolution label a pet uses, or a plain level for everything else.
 *
 * Pets call their levels `MK I` / `EV. II`, and a card showing "Nível 2" for a
 * pet would be using a word the rest of the game does not.
 */
export function itemLevelLabel(definitionId: string, level: number): string {
  const pet = findPet(definitionId);
  const form = pet?.forms[level - 1];
  return form ? form.name : `Nível ${level}`;
}

/** A short line under the name: what kind of thing this is. */
export function itemCategoryLabel(definitionId: string): string {
  const definition = findItemDefinition(definitionId);
  switch (definition?.category) {
    case 'ship':
      return 'Nave';
    case 'cannon':
      return 'Canhão';
    case 'artifact':
      return 'Artefato';
    case 'equipment':
      return 'Equipamento';
    case 'pet':
      return 'Pet';
    case 'consumable':
      return 'Consumível';
    default:
      return '';
  }
}

/** Everything a card needs, resolved once. */
export interface ItemDisplay {
  readonly name: string;
  readonly category: string;
  readonly levelLabel: string;
  /**
   * The manifest asset id, or `null` when no art exists.
   *
   * Resolved HERE so a caller never has to decide whether the definition id
   * doubles as an asset id - for pets and for the appearance consumables it
   * does not, and assuming it did is what put a `?` on a marketplace card.
   */
  readonly assetKey: string | null;
}

export function describeItem(definitionId: string, level = 1): ItemDisplay {
  return {
    name: itemDisplayName(definitionId),
    category: itemCategoryLabel(definitionId),
    levelLabel: itemLevelLabel(definitionId, level),
    assetKey: itemAssetKey(definitionId, level),
  };
}

/* -------------------------------------------------------------------------- */
/*  Searching by the name the player can actually see                         */
/* -------------------------------------------------------------------------- */

/**
 * Every combining mark, written as a Unicode property rather than as a literal
 * range: a literal [U+0300-U+036F] class is the usual idiom, but it puts invisible
 * characters in the source that no reviewer can see and that an editor can mangle.
 */
const NON_SPACING_MARKS = /\p{Mn}/gu;

/**
 * The comparison key: lower case, and no accents.
 *
 * "Apache Ômega" has to be found by somebody typing `apache omega` on a
 * keyboard that makes `Ô` awkward, and by somebody typing `OMEGA`. NFD splits
 * a letter from its diacritic and this strips the diacritic, so the two
 * spellings collapse onto one key.
 */
export function itemSearchKey(text: string): string {
  return text.normalize('NFD').replace(NON_SPACING_MARKS, '').toLowerCase();
}

/** Longer than any real item name; a bound rather than a preference. */
export const ITEM_SEARCH_MAX_LENGTH = 64;

/**
 * Which definitions a player means when they type a name.
 *
 * The inverse of `itemDisplayName`, and deliberately in the same file: a
 * marketplace that answers only to `pet_apache_prismatic` is a marketplace
 * nobody can search, and a search resolved against a SEPARATE list of names
 * would drift from the one the cards print.
 *
 * The result is a set of definition ids, which is what a query can filter on
 * with an index - never a pattern built from player input, and never "load
 * every listing and filter in the browser".
 *
 * An empty query returns `null`: no filter at all, which is not the same
 * answer as "nothing matched" (an empty array).
 */
export function definitionIdsMatchingName(query: string): readonly string[] | null {
  const needle = itemSearchKey(query.trim().slice(0, ITEM_SEARCH_MAX_LENGTH));
  if (needle.length === 0) {
    return null;
  }
  return ITEM_DEFINITIONS.filter((definition) =>
    itemSearchKey(itemDisplayName(definition.id)).includes(needle),
  ).map((definition) => definition.id);
}

/** Every catalog definition that has no name yet. Used by a test, not by a UI. */
export function definitionsWithoutDisplayName(
  definitions: readonly ItemDefinition[],
): readonly string[] {
  return definitions
    .filter((definition) => itemDisplayName(definition.id) === UNKNOWN_ITEM_NAME)
    .map((definition) => definition.id);
}
